// RoleDetailsModal.jsx
// Extracted from TeamAssemblyWorkspace.jsx — Role Details Dialog.
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import EmployeeNameLink from '../common/EmployeeNameLink';
import { TABS } from '../cards/EmployeeExpandedView';
import { COLOR_GOOD, BRAND_PURPLE } from '../../utils/colors';

export default function RoleDetailsModal({ open, onClose, role }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {role?.role_name || 'Role Details'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {role && (
          <Box>
            {/* Role Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Level:</strong> {role.level}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Hours:</strong> {role.total_hours ? `${role.total_hours} total hours` : 'Not specified'} • <strong>Rate:</strong> {role.hourly_rate ? `$${role.hourly_rate}/hr` : 'Not specified'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Timeline:</strong> {role.role_start_date || 'TBD'} to {role.role_end_date || 'TBD'}
              </Typography>
              {role.total_hours && role.hourly_rate && (
                <Typography variant="body1" sx={{ mb: 1, color: COLOR_GOOD, fontWeight: 'bold' }}>
                  <strong>Estimated Revenue:</strong> ${(role.total_hours * role.hourly_rate).toLocaleString()}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Required Skills */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Required Skills ({role.skills.length})
              </Typography>
              {role.skills.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {role.skills.map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill}
                      sx={{
                        bgcolor: BRAND_PURPLE,
                        color: 'black',
                        fontWeight: 'medium',
                        border: 'none'
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No specific skills defined for this role
                </Typography>
              )}
            </Box>

            {/* Assignment Status */}
            {role.status === 'assigned' && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Current Assignment
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                      {role.assigned_employee_name?.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        <EmployeeNameLink
                          employee={{ employee_id: role.assigned_employee_id, name: role.assigned_employee_name }}
                          tab={TABS.ENGAGEMENTS}
                        />
                      </Typography>
                      {role.fit_score && (
                        <Typography variant="body2" sx={{ color: COLOR_GOOD, fontWeight: 'bold' }}>
                          Fit Score: {role.fit_score}/10
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

RoleDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  role: PropTypes.object,
};
